import { JSX, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExportFormatType } from "@app/exports/enumerations";
import { ExportNetworkIntegrityGapImpactReportModel } from "@app/exports/exportModels";
import { reportName as reportNameHelper } from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import { SelectFormat } from "@app/exports/export-panel-body/filters";
import * as toast from "@app/exports/export-panel-body/toast";
import { GenerateBody } from "@app/projects/network-assessment/network-integrity/exports/GenerateBody";
import { NetworkIntegrityGapImpactReportRouteState, RouteMatchParams } from "@app/projects/network-assessment/network-integrity/models";
import ExportService from "@app/exports/exportService";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";

interface NetworkIntegrityGapImpactReportPanelBodyProps extends RouteMatchParams {
  isProviderDetailAvailable: boolean;
}

// Keyed by the NetworkIntegrityAdequacyImpactType (a.k.a. NetworkIntegrityImpactProviderKey) string value.
// Uses string keys rather than the enum so the Critical Alerts Removed scenario is supported ahead of the
// separate ticket that surfaces its bar/route and adds the enum member.
const gapImpactTypeLabels: Record<string, string> = {
  AlertGaps: "All Alerts Removed",
  AlertOrWarningGaps: "Alerts and Warnings Removed",
  CriticalAlertGaps: "Critical Alerts Removed",
};

const getGapImpactTypeLabel = (impactType: string): string => gapImpactTypeLabels[impactType] ?? impactType;

const NetworkIntegrityGapImpactReportPanelBody = (props: NetworkIntegrityGapImpactReportPanelBodyProps): JSX.Element => {

  const exportService = new ExportService();
  const locationState = useLocation<NetworkIntegrityGapImpactReportRouteState>().state;
  const defaultReportName = reportNameHelper(`Network Integrity Gap Impact - ${getGapImpactTypeLabel(props.impactType)}`);

  const [emailOnSuccess, setEmailOnSuccess] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [includeProviderDetails, setIncludeProviderDetails] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [outputFormat, setOutputFormat] = useState<ExportFormatType>(ExportFormatType.CSV);
  const [reportName, setReportName] = useState<string>(defaultReportName);
  const [sharedUsers, setSharedUsers] = useState<number[]>([]);
  const exportDataRowCount = locationState?.reportRowCount ?? 0;
  const excelRowLimit = 1048570; // Excel row limit is 1048576, limiting to just under that to allow for header rows

  const handleEmailOnSuccessChange = (value: boolean) => {
    setEmailOnSuccess(value);
  };

  const handleGenerateClick = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    const requestModel: ExportNetworkIntegrityGapImpactReportModel = {
      eMailOnSuccess: emailOnSuccess,
      exportName: reportName,
      formatType: outputFormat,
      impactProviderKey: props.impactType,
      includeProviderDetails: props.isProviderDetailAvailable && includeProviderDetails,
      projectId: Number.parseInt(props.projectId),
      projectRunConfigurationId: Number.parseInt(props.projectRunConfigurationId),
      providerType: props.providerType,
      regionSetIds: locationState?.regionSetIds ?? [],
      runAsynchronous: true,
      searchText: locationState?.searchText ?? "",
      showNewGapsOnly: locationState?.showNewGapsOnly ?? false,
      specialtyCodes: locationState?.specialtyCodes ?? [],
    };

    try {
      await exportService.postExportNetworkIntegrityGapImpact(requestModel);
      toast.toastInfo(reportName);
    } catch {
      setError("An error occurred while generating the report. Please try again.");
    }
  }, [props.projectId, props.projectRunConfigurationId, props.providerType, props.impactType, props.isProviderDetailAvailable, includeProviderDetails, outputFormat, sharedUsers, reportName, emailOnSuccess, locationState]);

  const handleReportNameChange = useCallback((value: string) => {
    setReportName(value);
  }, []);

  const handleSharedUserChange = useCallback((users: number[]) => {
    setSharedUsers(users);
  }, []);

  const handleOutputFormatChange = useCallback((value: ExportFormatType) => {
    setOutputFormat(value);
  }, []);

  return (
    <>
      <div className="row">
        {!!error && <QESAlert classNames="col-12" type="danger" persist={true}>{error}</QESAlert>}
      </div>
      <div className="row">
        <div className="col-6 col-sm-3">
          <SelectFormat
            excelDisabled={exportDataRowCount > excelRowLimit}
            exportType="network-integrity-gap-impact"
            outputFormat={outputFormat}
            onOutputFormatChange={handleOutputFormatChange} />
          {exportDataRowCount > excelRowLimit && <p className="text-danger"><i>Results are too large for Microsoft Excel Workbook (.xlsx).</i></p>}
        </div>
      </div>
      {props.isProviderDetailAvailable && (
        <div className="row mt-3">
          <div className="col-12">
            <h4 className="header-s">Column Configuration</h4>
            <QESCheckbox
              id="include-provider-details"
              testId="network-integrity-gap-impact__include-provider-details"
              label="Include Provider Details"
              checked={includeProviderDetails}
              onChange={(e) => setIncludeProviderDetails(e.target.checked)}
            />
          </div>
        </div>
      )}
      <GenerateBody
        {...props}
        emailOnSuccess={emailOnSuccess}
        isLoading={isLoading}
        onEmailOnSuccessChange={handleEmailOnSuccessChange}
        onGenerateClick={handleGenerateClick}
        onReportNameChange={handleReportNameChange}
        onSharedUsersChange={handleSharedUserChange}
        reportName={reportName}
        sharedUsers={sharedUsers}
      />
    </>
  );
}

export { NetworkIntegrityGapImpactReportPanelBody, getGapImpactTypeLabel };
